<!--
THESIS: Brickell is a living civic landmark with a collision-safe flat atlas users can choose without losing operational truth.
OWN-WORLD: Deep harbor blue, weathered concrete, oxidized marine metal, wet asphalt, amber road consequence, and opaque ivory instruments.
STORY: Know whether Brickell carries road traffic, see all nine spans, then follow only a selected vessel without losing reported-position truth.
FIRST VIEWPORT: Full-bleed river and bay from west-southwest of Brickell, with an edge-attached status instrument, opposite traffic rail, and continuous nine-station navigator.
FORM: User-approved Living Harbor Composition B; implementation audit seed 6c4c1bea.
FINISH: unreviewed and undocumented is unfinished; this build ends with the finish review, the verdict, DESIGN.md, and every shipping raster carrying its provenance.
-->
<script lang="ts">
  import { onMount } from "svelte";
  import { RefreshCw, ChevronDown, ChevronUp, History, CircleHelp } from "lucide-svelte";
  import type { BridgeId, PublicSnapshot, PublicVesselSummary } from "./shared/contracts";
  import { BRIDGE_STATIONS } from "./shared/corridor";
  import { ageSnapshot } from "./lib/client/snapshot-age";
  import { LiveConnection, type ConnectionState } from "./lib/client/live";
  import { inboundBayApproachVessels } from "./lib/bay-approach";
  import CorridorMap from "./lib/components/CorridorMap.svelte";
  import BridgeNavigator from "./lib/components/BridgeNavigator.svelte";
  import ForecastPanel from "./lib/components/ForecastPanel.svelte";
  import HistoryDrawer from "./lib/components/HistoryDrawer.svelte";
  import LiveTrafficPanel from "./lib/components/LiveTrafficPanel.svelte";
  import VesselDrawer from "./lib/components/VesselDrawer.svelte";
  import VesselLegendDrawer from "./lib/components/VesselLegendDrawer.svelte";
  import { formatDistanceToBrickell, formatEtClock, freshnessLabel } from "./lib/format";
  import { LIVING_HARBOR_STARTUP_TIMEOUT_MS } from "./lib/renderer-startup";
  import { completeBridgeList, unavailableSnapshot } from "./lib/unavailable";
  import type { PerformanceLoadClass, PerformanceRunReport } from "./lib/scene/performance";

  type RendererPreference = "auto" | "legacy" | "living";
  type RendererChoice = Exclude<RendererPreference, "auto">;
  type RendererPhase = "resolving" | "flat" | "living-loading" | "living-ready";
  type EnhancedUnavailableReason = "forced-colors" | "reduced-data" | "webgl2";
  type LivingRiverComponentType = typeof import("./lib/components/living/LivingRiver.svelte").default;

  const RENDERER_STORAGE_KEY = "miami-bridges:renderer";
  const QA_TOOLS_ENABLED = import.meta.env.DEV || import.meta.env.VITE_LIVING_QA === "true";

  let receivedData: PublicSnapshot = unavailableSnapshot();
  let snapshot: PublicSnapshot = receivedData;
  let connectionState: ConnectionState = "connecting";
  let receivedSnapshot = false;
  let now = new Date();
  let historyOpen = false;
  let vesselLegendOpen = false;
  let selectedVessel: PublicVesselSummary | null = null;
  let selectedBridgeId: BridgeId = "brickell";
  let bridgeViewRevision = 0;
  let expiredVesselAnchor: PublicVesselSummary | null = null;
  let drawerTrigger: HTMLElement | null = null;
  let historyButton: HTMLButtonElement;
  let trafficInstrument: HTMLDivElement;
  let liveConnection: LiveConnection | null = null;
  let snapshotReceivedAtMs = Date.now();
  let clientReady = false;
  let enhancedSupported = false;
  let enhancedUnavailableReason: EnhancedUnavailableReason | null = null;
  let enhancedFailure: string | null = null;
  let rendererPreference: RendererPreference = "auto";
  let rendererPhase: RendererPhase = "resolving";
  let rendererAttempt = 0;
  let rendererStartupTimeout: number | null = null;
  let reducedMotion = false;
  let livingLoading = false;
  let livingModulePromise: Promise<LivingRiverComponentType> | null = null;
  let livingReadyCallback: () => void = () => undefined;
  let livingFailureCallback: (reason: string) => void = () => undefined;
  let LivingRiverComponent: LivingRiverComponentType | null = null;
  let sceneAnnouncement = "";
  let performanceScenarioId = "live";
  let performanceLoadClass: PerformanceLoadClass = "normal";
  let performanceReport: PerformanceRunReport | null = null;
  let sceneClockMs: number | null = null;
  let compactLayout = false;
  let portraitLayout = false;
  let mobilePanel: "status" | "traffic" | null = null;
  let trafficToggle: HTMLButtonElement;

  function mobileEscape(event: KeyboardEvent): void {
    if (event.key !== "Escape" || !compactLayout || !mobilePanel || historyOpen || vesselLegendOpen || selectedVessel) return;
    event.preventDefault();
    const panel = mobilePanel;
    mobilePanel = null;
    if (panel === "traffic") trafficToggle?.focus();
    else document.querySelector<HTMLButtonElement>(".forecast__toggle")?.focus();
  }

  function toggleMobilePanel(panel: "status" | "traffic"): void {
    mobilePanel = mobilePanel === panel ? null : panel;
  }

  $: snapshot = receivedSnapshot
    ? ageSnapshot(receivedData, Math.max(0, now.getTime() - snapshotReceivedAtMs))
    : receivedData;
  $: if (receivedSnapshot) {
    syncSelectedVessel(snapshot);
    reconcileRenderer();
  }

  $: degraded = receivedSnapshot
    ? snapshot.forecast.freshness !== "live"
    : connectionState !== "connecting" && connectionState !== "live";
  $: completeBridges = completeBridgeList(snapshot.bridges);
  $: brickell = completeBridges[0]!;
  $: previewMode = receivedSnapshot && snapshot.dataMode === "demo";
  $: bayApproachVessels = inboundBayApproachVessels(snapshot.vessels);
  $: productModeLabel = previewMode ? "River status preview" : "Live river status";
  $: activeTrackLabel = previewMode ? "sample" : snapshot.vesselActivity.freshness === "stale" ? "last reported" : "active";
  $: vesselSignalLabel = previewMode
    ? "sample"
    : snapshot.vesselActivity.status === "receiving"
      ? "receiving positions"
      : snapshot.vesselActivity.status === "connected"
        ? "connected · no recent positions"
        : snapshot.vesselActivity.status === "connecting"
          ? "connecting"
          : snapshot.vesselActivity.status === "reconnecting"
            ? "reconnecting"
            : snapshot.vesselActivity.status === "disabled"
              ? "tracking off"
              : "tracking unavailable";
  $: vesselActivityAria = `${vesselSignalLabel} vessel tracking: ${snapshot.vesselActivity.activeTracks} ${activeTrackLabel} vessel${snapshot.vesselActivity.activeTracks === 1 ? "" : "s"}; ${snapshot.vesselActivity.approachingBridge} relevant to Brickell ${snapshot.vesselActivity.freshness === "stale" ? "in the last report" : "now"}. Only vessels recently transmitting a usable location are counted. Some vessels do not transmit a location or vessel class.`;
  $: degradedCopy =
    snapshot.forecast.freshness === "delayed"
      ? "Live readings are delayed. Times and positions may lag."
      : snapshot.forecast.freshness === "stale"
        ? "Readings are stale. Confirm conditions before choosing a crossing."
        : "Some current readings cannot be confirmed.";

  $: connectionLabel =
    previewMode
      ? "Demo data"
      : connectionState === "live"
      ? "Live"
      : connectionState === "polling"
        ? "Reconnecting"
        : connectionState === "offline"
          ? "Connection interrupted"
          : "Connecting";
  $: livingPermitted = snapshot.experience.livingRiverEnabled || QA_TOOLS_ENABLED;
  $: effectiveRenderer = rendererPhase === "flat" ? "legacy" : "living";
  $: livingChoiceAvailable = clientReady && livingPermitted && enhancedSupported;
  $: rendererPreparing = rendererPhase === "resolving" || rendererPhase === "living-loading";
  $: livingChoiceTitle = rendererPreparing
    ? "3D river view is preparing"
    : livingChoiceAvailable
    ? "Show the 3D river view"
    : livingUnavailableMessage(livingPermitted, enhancedUnavailableReason);
  $: enhancedActive = rendererPhase === "living-ready" && Boolean(LivingRiverComponent);
  $: livingMounted = (rendererPhase === "living-loading" || rendererPhase === "living-ready")
    && Boolean(LivingRiverComponent);
  $: flatActive = rendererPhase === "flat";
  $: harborSceneLabel = rendererPhase === "flat"
    ? "2D Miami River operational diagram"
    : rendererPhase === "living-ready"
      ? "3D Miami River scene"
      : "Preparing Living Harbor";
  $: loadingDetail = !receivedSnapshot
    ? "Aligning live river status"
    : !LivingRiverComponent
      ? "Loading bridge and vessel geometry"
      : "Bringing Brickell into view";
  $: harborLoaderDay = snapshot.weather.isDay ?? isEtDay(now);

  function openHistory(): void {
    drawerTrigger = historyButton;
    selectedVessel = null;
    vesselLegendOpen = false;
    historyOpen = true;
  }

  function openVesselLegend(trigger: HTMLElement): void {
    drawerTrigger = trigger;
    historyOpen = false;
    selectedVessel = null;
    vesselLegendOpen = true;
  }

  function openVessel(vessel: PublicVesselSummary, trigger: HTMLElement): void {
    drawerTrigger = trigger;
    historyOpen = false;
    vesselLegendOpen = false;
    sceneAnnouncement = "";
    expiredVesselAnchor = null;
    selectedBridgeId = "brickell";
    selectedVessel = vessel;
  }

  function selectBridge(bridgeId: BridgeId, trigger: HTMLButtonElement): void {
    drawerTrigger = trigger;
    selectedVessel = null;
    expiredVesselAnchor = null;
    historyOpen = false;
    vesselLegendOpen = false;
    selectedBridgeId = bridgeId;
    mobilePanel = null;
    bridgeViewRevision += 1;
    const station = BRIDGE_STATIONS.find((candidate) => candidate.id === bridgeId);
    sceneAnnouncement = `Showing ${station?.name ?? "the selected bridge"} in the river view.`;
  }

  function resetLivingView(): void {
    selectedVessel = null;
    expiredVesselAnchor = null;
    selectedBridgeId = "brickell";
    bridgeViewRevision += 1;
    sceneAnnouncement = "Returned to the default Brickell Bridge view.";
  }

  function closeDrawer(): void {
    historyOpen = false;
    vesselLegendOpen = false;
    selectedVessel = null;
  }

  async function loadLivingRiver(): Promise<void> {
    if (LivingRiverComponent || livingLoading) return;
    livingLoading = true;
    livingModulePromise ??= import("./lib/components/living/LivingRiver.svelte")
      .then((module) => module.default);
    try {
      LivingRiverComponent = await livingModulePromise;
    } catch (error) {
      livingModulePromise = null;
      livingFailed(error instanceof Error ? error.message : "The enhanced river could not load");
    } finally {
      livingLoading = false;
    }
  }

  function livingFailed(reason: string): void {
    if (rendererPhase !== "living-loading" && rendererPhase !== "living-ready") return;
    enhancedFailure = reason;
    showFlatRenderer();
    sceneAnnouncement = "The enhanced river view is unavailable. The complete operational diagram is now shown.";
  }

  function clearRendererStartupTimeout(): void {
    if (rendererStartupTimeout === null) return;
    window.clearTimeout(rendererStartupTimeout);
    rendererStartupTimeout = null;
  }

  function armRendererStartupTimeout(): void {
    if (!clientReady || rendererStartupTimeout !== null || typeof window === "undefined") return;
    rendererStartupTimeout = window.setTimeout(() => {
      rendererStartupTimeout = null;
      if (rendererPhase !== "resolving" && rendererPhase !== "living-loading") return;
      enhancedFailure = "Living Harbor did not become ready after 30 seconds";
      showFlatRenderer();
      sceneAnnouncement = "Living Harbor did not become ready. The complete operational diagram is now shown.";
    }, LIVING_HARBOR_STARTUP_TIMEOUT_MS);
  }

  function beginLivingRenderer(): void {
    if (rendererPhase === "living-loading" || rendererPhase === "living-ready") return;
    const attempt = ++rendererAttempt;
    rendererPhase = "living-loading";
    armRendererStartupTimeout();
    livingFailureCallback = (reason) => {
      if (rendererAttempt !== attempt) return;
      livingFailed(reason);
    };
    livingReadyCallback = () => {
      if (rendererAttempt !== attempt || rendererPhase !== "living-loading") return;
      clearRendererStartupTimeout();
      const reveal = () => {
        if (rendererAttempt !== attempt || rendererPhase !== "living-loading") return;
        rendererPhase = "living-ready";
      };
      if (typeof requestAnimationFrame === "function") requestAnimationFrame(reveal);
      else queueMicrotask(reveal);
    };
    void loadLivingRiver();
  }

  function showFlatRenderer(): void {
    clearRendererStartupTimeout();
    if (rendererPhase !== "flat") rendererAttempt += 1;
    rendererPhase = "flat";
    livingReadyCallback = () => undefined;
    livingFailureCallback = () => undefined;
  }

  function waitForRendererDecision(): void {
    if (rendererPhase !== "resolving") rendererAttempt += 1;
    rendererPhase = "resolving";
    livingReadyCallback = () => undefined;
    livingFailureCallback = () => undefined;
    armRendererStartupTimeout();
  }

  function reconcileRenderer(): void {
    if (!clientReady) return;
    if (rendererPreference === "legacy" || !enhancedSupported) {
      showFlatRenderer();
      return;
    }
    if (rendererPreference === "auto" && !receivedSnapshot) {
      waitForRendererDecision();
      return;
    }
    if (rendererPreference === "living" && !receivedSnapshot && !QA_TOOLS_ENABLED) {
      waitForRendererDecision();
      return;
    }

    const permitted = snapshot.experience.livingRiverEnabled || QA_TOOLS_ENABLED;
    const wantsLiving = rendererPreference === "living"
      || (rendererPreference === "auto" && snapshot.experience.livingRiverEnabled);
    if (!permitted || !wantsLiving || enhancedFailure) {
      showFlatRenderer();
      return;
    }
    beginLivingRenderer();
  }

  function rendererFromValue(value: string | null): RendererChoice | null {
    return value === "legacy" || value === "living" ? value : null;
  }

  function storedRenderer(): RendererChoice | null {
    try {
      return rendererFromValue(window.localStorage.getItem(RENDERER_STORAGE_KEY));
    } catch {
      return null;
    }
  }

  function rendererFromLocation(): RendererChoice | null {
    return rendererFromValue(new URLSearchParams(window.location.search).get("renderer"));
  }

  function persistRenderer(choice: RendererChoice): void {
    try {
      window.localStorage.setItem(RENDERER_STORAGE_KEY, choice);
    } catch {
      // URL state remains authoritative when storage is unavailable.
    }
    const url = new URL(window.location.href);
    url.searchParams.set("renderer", choice);
    window.history.replaceState(window.history.state, "", url);
  }

  function selectRenderer(choice: RendererChoice): void {
    if (choice === "living" && !livingChoiceAvailable) {
      sceneAnnouncement = livingUnavailableMessage(livingPermitted, enhancedUnavailableReason);
      return;
    }
    if (choice === "living") enhancedFailure = null;
    rendererPreference = choice;
    persistRenderer(choice);
    reconcileRenderer();
    sceneAnnouncement = choice === "living"
      ? "3D river view selected."
      : "2D river view selected.";
  }

  function livingUnavailableMessage(
    permitted: boolean,
    reason: EnhancedUnavailableReason | null,
  ): string {
    if (!permitted) {
      return "3D view is not enabled for this release. The complete 2D view remains available.";
    }
    if (reason === "forced-colors") {
      return "3D view is disabled while forced colors is active. The complete 2D view remains available.";
    }
    if (reason === "reduced-data") {
      return "3D view is disabled by this device's reduced-data preference. The complete 2D view remains available.";
    }
    if (reason === "webgl2") {
      return "3D view is unavailable because this browser cannot start WebGL 2. The complete 2D view remains available.";
    }
    return "3D view is still preparing. The complete 2D view remains available.";
  }

  function supportsWebGl2(): boolean {
    if (typeof WebGL2RenderingContext === "undefined") return false;
    try {
      const canvas = document.createElement("canvas");
      return Boolean(canvas.getContext("webgl2"));
    } catch {
      return false;
    }
  }

  function isEtDay(date: Date): boolean {
    const hour = Number(new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "numeric",
      hourCycle: "h23",
    }).format(date));
    return hour >= 6 && hour < 19;
  }

  function acceptSnapshot(nextSnapshot: PublicSnapshot, receivedAtMs = Date.now()): void {
    snapshotReceivedAtMs = receivedAtMs;
    // Preserve every reported fix even when Svelte batches consecutive publications.
    syncSelectedVessel(nextSnapshot);
    receivedData = nextSnapshot;
    receivedSnapshot = true;
  }

  function syncSelectedVessel(nextSnapshot: PublicSnapshot): void {
    if (expiredVesselAnchor && nextSnapshot.vessels.some((vessel) =>
      vessel.id === expiredVesselAnchor?.id)) {
      expiredVesselAnchor = null;
    }
    if (selectedVessel) {
      const refreshed = nextSnapshot.vessels.find((vessel) => vessel.id === selectedVessel?.id);
      if (refreshed) selectedVessel = refreshed;
      else {
        sceneAnnouncement = `${selectedVessel.name ?? "The selected vessel"} no longer has a current position. Returning to Brickell.`;
        expiredVesselAnchor = selectedVessel;
        selectedVessel = null;
      }
    }
  }

  function startLiveConnection(): void {
    liveConnection = new LiveConnection({
      onSnapshot: acceptSnapshot,
      onState: (state) => (connectionState = state),
    });
    liveConnection.start();
  }

  onMount(() => {
    rendererPreference = rendererFromLocation() ?? storedRenderer() ?? "auto";
    const reducedMotionMedia = window.matchMedia("(prefers-reduced-motion: reduce)");
    const forcedColorsMedia = window.matchMedia("(forced-colors: active)");
    const compactMedia = window.matchMedia("(max-width: 780px), (max-height: 560px) and (orientation: landscape)");
    const portraitMedia = window.matchMedia("(max-width: 780px) and (orientation: portrait)");
    const syncLayout = () => { compactLayout = compactMedia.matches; portraitLayout = portraitMedia.matches; };
    syncLayout();
    compactMedia.addEventListener("change", syncLayout);
    portraitMedia.addEventListener("change", syncLayout);
    const syncReducedMotion = () => (reducedMotion = reducedMotionMedia.matches);
    const syncEnhancedSupport = () => {
      const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
      enhancedUnavailableReason = forcedColorsMedia.matches
        ? "forced-colors"
        : connection?.saveData === true
          ? "reduced-data"
          : supportsWebGl2()
            ? null
            : "webgl2";
      enhancedSupported = enhancedUnavailableReason === null;
      reconcileRenderer();
    };
    syncReducedMotion();
    syncEnhancedSupport();
    reducedMotionMedia.addEventListener("change", syncReducedMotion);
    forcedColorsMedia.addEventListener("change", syncEnhancedSupport);
    const clock = window.setInterval(() => {
      if (sceneClockMs === null) now = new Date();
    }, 1_000);
    let disposed = false;
    const scenarioQuery = new URLSearchParams(window.location.search).get("scenario");
    if (QA_TOOLS_ENABLED && scenarioQuery) {
      void import("./lib/scene/deterministic-scenarios").then(({ deterministicScenarioFromQuery }) => {
        if (disposed) return;
        const scenario = deterministicScenarioFromQuery(window.location.search);
        if (!scenario) {
          clientReady = true;
          reconcileRenderer();
          startLiveConnection();
          return;
        }
        performanceScenarioId = scenario.id;
        performanceLoadClass = scenario.loadClass;
        sceneClockMs = scenario.clockMs;
        now = new Date(scenario.clockMs);
        connectionState = "live";
        acceptSnapshot(scenario.snapshot, scenario.clockMs);
        clientReady = true;
        reconcileRenderer();
      }).catch(() => {
        if (disposed) return;
        clientReady = true;
        reconcileRenderer();
        startLiveConnection();
      });
    } else {
      clientReady = true;
      reconcileRenderer();
      startLiveConnection();
    }
    const performanceListener = (event: Event) => {
      if (event instanceof CustomEvent) performanceReport = event.detail as PerformanceRunReport;
    };
    window.addEventListener("living-harbor:performance-report", performanceListener);
    const handlePopState = () => {
      rendererPreference = rendererFromLocation() ?? "auto";
      enhancedFailure = null;
      reconcileRenderer();
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      disposed = true;
      compactMedia.removeEventListener("change", syncLayout);
      portraitMedia.removeEventListener("change", syncLayout);
      window.clearInterval(clock);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("living-harbor:performance-report", performanceListener);
      reducedMotionMedia.removeEventListener("change", syncReducedMotion);
      forcedColorsMedia.removeEventListener("change", syncEnhancedSupport);
      clearRendererStartupTimeout();
      liveConnection?.stop();
    };
  });
</script>

<svelte:window onkeydown={mobileEscape} />

<svelte:head>
  <meta name="color-scheme" content="dark" />
</svelte:head>

<div class="app-shell">
  <header class="masthead">
    <div class="masthead__identity">
      <a class="wordmark" href="#live-corridor" aria-label="Miami Bridges home">MIAMI BRIDGES</a>
      <span class="product-mode">{productModeLabel}</span>
    </div>

    <div class="masthead__rail">
      <span class:live={connectionState === "live" && !previewMode} class:preview={previewMode} class="connection" aria-live="polite">
        <i aria-hidden="true"></i>{connectionLabel}
      </span>
      <div
        class:vessel-count--live={snapshot.vesselActivity.freshness === "live"}
        class:vessel-count--delayed={snapshot.vesselActivity.freshness === "delayed"}
        class="vessel-count"
        aria-live="polite"
        aria-label={vesselActivityAria}
        title="Only vessels recently transmitting a usable location are counted. Some vessels do not transmit a location or vessel class."
      >
        <div class="vessel-count__metrics" aria-hidden="true">
          <span class="vessel-count__metric"><strong>{snapshot.vesselActivity.activeTracks}</strong><span>{activeTrackLabel}</span></span>
          <span class="vessel-count__metric"><strong>{snapshot.vesselActivity.approachingBridge}</strong><span>Brickell</span></span>
        </div>
        <span class="vessel-count__note" aria-hidden="true"><i></i>* {vesselSignalLabel} · location only · class may be unknown</span>
      </div>
      <div class="clock" aria-label={`Local time ${formatEtClock(now)}`}>
        <time datetime={now.toISOString()}>{formatEtClock(now)}</time>
      </div>
      <div class="renderer-switch" role="group" aria-label="River view">
        <button
          type="button"
          aria-pressed={effectiveRenderer === "living"}
          aria-busy={rendererPreparing}
          aria-disabled={!livingChoiceAvailable}
          title={livingChoiceTitle}
          onclick={() => selectRenderer("living")}
        >3D</button>
        <button
          type="button"
          aria-pressed={effectiveRenderer === "legacy"}
          onclick={() => selectRenderer("legacy")}
        >2D</button>
      </div>
      <button class="history-button" bind:this={historyButton} type="button" aria-label="History" onclick={openHistory}>
        <History class="history-button__icon" size={19} aria-hidden="true" />
        <span>History</span>
      </button>
    </div>
  </header>

  {#if degraded}
    <div class="service-notice" role="status">
      <span><strong>{freshnessLabel(snapshot.forecast.freshness, snapshot.dataMode)} readings.</strong> {degradedCopy}</span>
      {#if connectionState !== "live"}
        <button type="button" onclick={() => liveConnection?.retry()}><RefreshCw size={16} /> Retry</button>
      {/if}
    </div>
  {/if}

  <p class="sr-only" aria-live="assertive">{sceneAnnouncement}</p>

  <main
    id="live-corridor"
    class:harbor--enhanced={enhancedActive}
    class:harbor--loading={rendererPreparing}
    class:harbor--compact={compactLayout}
    class:harbor--panel-open={compactLayout && mobilePanel !== null}
    class="harbor"
  >
    <div class="harbor__scene" aria-label={harborSceneLabel}>
      <div
        class:harbor__fallback--hidden={!flatActive}
        class="harbor__fallback"
        aria-hidden={!flatActive}
        inert={!flatActive}
      >
        <CorridorMap
          active={flatActive}
          compact={portraitLayout}
          {selectedBridgeId}
          viewRevision={bridgeViewRevision}
          bridges={completeBridges}
          vessels={snapshot.vessels}
          vesselGroups={snapshot.vesselGroups}
          selectedVesselId={selectedVessel?.id ?? null}
          dataMode={snapshot.dataMode}
          onvesselselect={openVessel}
        />
      </div>

      {#if livingMounted && LivingRiverComponent}
        <LivingRiverComponent
          bridges={completeBridges}
          vessels={snapshot.vessels}
          vesselGroups={snapshot.vesselGroups}
          weather={snapshot.weather}
          roadTraffic={snapshot.roadTraffic}
          generatedAt={snapshot.generatedAt}
          {snapshotReceivedAtMs}
          {sceneClockMs}
          selectedVesselId={selectedVessel?.id ?? null}
          {selectedBridgeId}
          {bridgeViewRevision}
          {expiredVesselAnchor}
          {reducedMotion}
          {performanceScenarioId}
          {performanceLoadClass}
          onready={livingReadyCallback}
          onfailure={livingFailureCallback}
          onresetview={resetLivingView}
        />
      {/if}

      {#if !rendererPreparing}
        <div class="harbor__orientation" aria-hidden="true">
          <span>MIAMI RIVER · UPRIVER</span>
          <i></i>
          <span>BISCAYNE BAY · EAST</span>
        </div>
      {/if}

      {#if enhancedActive && !compactLayout && bayApproachVessels.length > 0}
        <nav class="bay-approach" aria-label="Inbound vessels approaching from Biscayne Bay">
          <div class="bay-approach__heading">
            <span>BAY APPROACH</span>
            <strong>{bayApproachVessels.length} INBOUND</strong>
          </div>
          {#each bayApproachVessels as vessel (vessel.id)}
            <button
              type="button"
              class:bay-approach__vessel--selected={selectedVessel?.id === vessel.id}
              class="bay-approach__vessel"
              aria-label={`Follow ${vessel.name ?? "unnamed vessel"}, ${formatDistanceToBrickell(vessel.distanceToBrickellMeters) ?? "Distance unavailable"}`}
              onclick={(event) => openVessel(vessel, event.currentTarget)}
            >
              <i aria-hidden="true"></i>
              <span>
                <strong>{vessel.name ?? "UNNAMED VESSEL"}</strong>
                <small>{formatDistanceToBrickell(vessel.distanceToBrickellMeters) ?? "Distance unavailable"}{vessel.etaMinutes === null ? "" : ` · ${Math.round(vessel.etaMinutes)} MIN ETA`}</small>
              </span>
              <b aria-hidden="true">FOLLOW →</b>
            </button>
          {/each}
        </nav>
      {/if}

      {#if rendererPreparing}
        <div
          class:harbor-loader--day={harborLoaderDay}
          class="harbor-loader"
          role="status"
          aria-live="polite"
        >
          <div class="harbor-loader__instrument">
            <strong>Preparing Living Harbor</strong>
            <span>{loadingDetail}</span>
            <div class="harbor-loader__route" aria-hidden="true">
              {#each completeBridges as bridge, index (bridge.id)}
                <i class:harbor-loader__beacon={index === 0}></i>
              {/each}
            </div>
            <small>
              <span>UPRIVER</span>
              <span>BRICKELL · MIAMI RIVER</span>
              <span>BAY</span>
            </small>
          </div>
        </div>
      {/if}

      {#if selectedVessel}
        <div class="follow-readout" role="status">
          <span>FOLLOWING</span>
          <strong>{selectedVessel.name ?? "UNNAMED VESSEL"}</strong>
          <span>HULL POSITION ESTIMATED FROM LAST REPORT</span>
          <i aria-hidden="true"></i>
          <span>AMBER RING = REPORTED POSITION</span>
        </div>
      {/if}

      {#if QA_TOOLS_ENABLED && new URLSearchParams(typeof location === "undefined" ? "" : location.search).has("diagnostics")}
        <output class="render-diagnostics">
          <strong>RENDER</strong>
          <span>{rendererPreparing ? "LOADING LIVING HARBOR" : enhancedActive ? "WEBGL2 · THRELTE" : "SVG FALLBACK"}</span>
          <span>{snapshot.vessels.length} HULLS · {completeBridges.length} SPANS</span>
          {#if enhancedActive && !performanceReport}<span>MEASURING FRAME BUDGET…</span>{/if}
          {#if performanceReport}
            <span>{performanceReport.metrics.fps?.toFixed(1) ?? "—"} FPS · P95 {performanceReport.metrics.p95FrameTimeMs?.toFixed(1) ?? "—"} MS</span>
            <span>{performanceReport.metrics.drawCalls} CALLS · {performanceReport.metrics.triangles.toLocaleString()} TRIANGLES</span>
            <span>{(performanceReport.metrics.estimatedGpuMemoryBytes / (1024 * 1024)).toFixed(1)} MIB EST. GPU · {performanceReport.evaluation.passed ? "WITHIN BUDGET" : "OVER BUDGET"}</span>
          {/if}
          {#if enhancedFailure}<span>FALLBACK: {enhancedFailure}</span>{/if}
        </output>
      {/if}
    </div>

    <section class:status-instrument--expanded={mobilePanel === "status"} class="status-instrument" aria-label="Brickell bridge and traffic status">
      <ForecastPanel forecast={snapshot.forecast} bridge={brickell} weather={snapshot.weather} roadTraffic={snapshot.roadTraffic} {now} dataMode={snapshot.dataMode} compact={compactLayout} expanded={mobilePanel === "status"} ontoggle={() => toggleMobilePanel("status")} />
    </section>

    <div
      class:traffic-instrument--expanded={mobilePanel === "traffic"}
      class="traffic-instrument"
      bind:this={trafficInstrument}
      role="region"
      aria-label="Brickell vessel traffic"
      tabindex="-1"
    >
      {#if compactLayout}
        <div class="mobile-traffic-toolbar">
        <button
          class="mobile-traffic-toggle"
          bind:this={trafficToggle}
          type="button"
          aria-expanded={mobilePanel === "traffic"}
          aria-controls="mobile-traffic-content"
          onclick={() => toggleMobilePanel("traffic")}
        >
          <span><strong>Vessel traffic</strong><span>{snapshot.vessels.length} {snapshot.vessels.length === 1 ? "vessel" : "vessels"} near Brickell{bayApproachVessels.length ? ` · ${bayApproachVessels.length} from the bay` : ""}</span></span>
          <span class="mobile-traffic-toggle__action">{mobilePanel === "traffic" ? "Hide" : "Show"}
            {#if mobilePanel === "traffic"}<ChevronDown size={18} aria-hidden="true" />{:else}<ChevronUp size={18} aria-hidden="true" />{/if}
          </span>
        </button>
        {#if mobilePanel === "traffic"}
          <button class="mobile-traffic-legend" type="button" aria-label="Vessel types" aria-expanded={vesselLegendOpen} onclick={(event) => openVesselLegend(event.currentTarget)}><CircleHelp size={20} aria-hidden="true" /></button>
        {/if}
        </div>
      {/if}
      <div id="mobile-traffic-content" class="traffic-instrument__content" hidden={compactLayout && mobilePanel !== "traffic"}>
      <LiveTrafficPanel
        vessels={snapshot.vessels}
        vesselGroups={snapshot.vesselGroups}
        bridgeState={brickell.state}
        selectedVesselId={selectedVessel?.id ?? null}
        dataMode={snapshot.dataMode}
        legendOpen={vesselLegendOpen}
        onlegendopen={openVesselLegend}
        onvesselselect={openVessel}
      />
      </div>
    </div>

    <BridgeNavigator
      bridges={completeBridges}
      {selectedBridgeId}
      onbridgeselect={selectBridge}
    />
  </main>
</div>

{#if historyOpen}
  <HistoryDrawer onclose={closeDrawer} returnFocus={drawerTrigger} />
{:else if vesselLegendOpen}
  <VesselLegendDrawer onclose={closeDrawer} returnFocus={drawerTrigger} />
{:else if selectedVessel}
  <VesselDrawer vessel={selectedVessel} onclose={closeDrawer} returnFocus={drawerTrigger} fallbackFocus={trafficInstrument} />
{/if}
